import Stack from '@mui/material/Stack';
import type { ReactNode } from 'react';
import type { Page } from '@/shared/api/page';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { ListPagination } from './ListPagination';

type QueryLike<T> = { data: Page<T> | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
type Paging = { page: number; pageSize: number; setPage: (p: number) => void; setPageSize: (s: number) => void };

type Props<T> = {
  query: QueryLike<T>;
  paging: Paging;
  empty: { title: string; hint?: string; action?: ReactNode };
  children: (items: T[], isLoading: boolean) => ReactNode;
};

/** One place for the four states of a server-paginated list: loading, error, empty and data. */
export function PagedSection<T>({ query, paging, empty, children }: Props<T>) {
  const { data } = query;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (data?.total === 0) return <EmptyState {...empty} />;
  return (
    <Stack sx={{ gap: 1 }}>
      {children(data?.items ?? [], query.isPending)}
      {data ? (
        <ListPagination total={data.total} page={paging.page} pageSize={paging.pageSize} onPageChange={paging.setPage} onPageSizeChange={paging.setPageSize} />
      ) : null}
    </Stack>
  );
}
